// Rechnung zusammenbauen + §11 UStG Validierung

import { toEuroCents, type EuroCents } from '../../../types/branded';
import { type Rechnung, type RechnungsPosition, type Rechnungsadresse, type MandantID } from '../types';
import { LEISTUNG_MAP } from '../data/leistungskatalog';

// ── EuroCents-Arithmetik (keine Float-Fehler) ─────────────────────────────

function addCents(...vals: EuroCents[]): EuroCents {
  return toEuroCents(vals.reduce((s, v) => s + v, 0));
}

function mulCents(cents: EuroCents, factor: number): EuroCents {
  return toEuroCents(Math.round(cents * factor));
}

// ── Rechnungsnummer generieren ────────────────────────────────────────────

export function genRechnungsNr(mandantKurz: string, laufNr: number): string {
  const year = new Date().getFullYear();
  return `${year}-${mandantKurz}-${String(laufNr).padStart(3, '0')}`;
}

// ── Position berechnen ────────────────────────────────────────────────────

export interface PositionInput {
  leistungCode: string;
  menge:        number;
  preisOverride?: EuroCents | undefined;
}

export function buildPosition(input: PositionInput): RechnungsPosition | null {
  const l = LEISTUNG_MAP.get(input.leistungCode);
  if (l === undefined) { return null; }

  const einzelNetto = input.preisOverride ?? l.preisNetto;
  const menge       = input.menge;
  const gesamtNetto = mulCents(einzelNetto, menge);
  const gesamtUst   = mulCents(gesamtNetto, l.ustSatz / 100);
  const gesamtBrutto = addCents(gesamtNetto, gesamtUst);

  return {
    leistungCode:      l.code,
    bezeichnung:       l.bezeichnung,
    menge,
    einheit:           l.einheit,
    einzelpreisNetto:  einzelNetto,
    ustSatz:           l.ustSatz,
    gesamtNetto,
    gesamtUst,
    gesamtBrutto,
  };
}

// ── Rechnung bauen ────────────────────────────────────────────────────────

export interface RechnungInput {
  mandantId:      MandantID;
  mandantKurz:    string;
  laufNr:         number;
  empfaenger:     Rechnungsadresse;
  positionen:     PositionInput[];
  leistungszeitraum?: string | undefined;
  anmerkungen?:       string | undefined;
  zahlungsfrist:  number;   // Tage
}

export function buildRechnung(input: RechnungInput): Rechnung {
  const heute    = new Date();
  const faellig  = new Date(heute);
  faellig.setDate(faellig.getDate() + input.zahlungsfrist);

  const positionen = input.positionen
    .map(p => buildPosition(p))
    .filter((p): p is RechnungsPosition => p !== null);

  const summeNetto   = addCents(...positionen.map(p => p.gesamtNetto));
  const summeUst     = addCents(...positionen.map(p => p.gesamtUst));
  const summeBrutto  = addCents(summeNetto, summeUst);

  return {
    mandantId:      input.mandantId,
    rechnungsNr:    genRechnungsNr(input.mandantKurz, input.laufNr),
    datum:          heute.toISOString().slice(0, 10),
    faelligAm:      faellig.toISOString().slice(0, 10),
    empfaenger:     input.empfaenger,
    positionen,
    summeNetto,
    summeUst,
    summeBrutto,
    status:         'entwurf',
    zahlungsbedingungen: `Zahlbar innerhalb von ${input.zahlungsfrist} Tagen ohne Abzug.`,
    leistungszeitraum:   input.leistungszeitraum,
    anmerkungen:         input.anmerkungen,
    erstelltAm:     heute.toISOString().slice(0, 10),
  };
}

// ── §11 UStG Pflicht-Validierung ──────────────────────────────────────────

export interface ValidationError {
  field:   string;
  message: string;
}

export function validateRechnung(r: Rechnung, uidAussteller: string): ValidationError[] {
  const errors: ValidationError[] = [];

  // §11 Abs. 1 Z 1: Name + Anschrift Aussteller
  if (uidAussteller.trim() === '') {
    errors.push({ field: 'uid_aussteller', message: 'UID-Nummer des Ausstellers fehlt (§11 (1) Z 3 UStG)' });
  }

  // §11 Abs. 1 Z 2: Name + Anschrift Empfänger
  if (r.empfaenger.firma.trim() === '') {
    errors.push({ field: 'empfaenger_firma', message: 'Firmenname des Empfängers fehlt (§11 (1) Z 2 UStG)' });
  }
  if (r.empfaenger.strasse.trim() === '' || r.empfaenger.ort.trim() === '') {
    errors.push({ field: 'empfaenger_adresse', message: 'Adresse des Empfängers unvollständig (§11 (1) Z 2 UStG)' });
  }

  // §11 Abs. 1 Z 4: Ausstellungsdatum
  if (r.datum === '') {
    errors.push({ field: 'datum', message: 'Ausstellungsdatum fehlt (§11 (1) Z 4 UStG)' });
  }

  // §11 Abs. 1 Z 5: Fortlaufende Nummer
  if (r.rechnungsNr.trim() === '') {
    errors.push({ field: 'rechnungsNr', message: 'Rechnungsnummer fehlt (§11 (1) Z 5 UStG)' });
  }

  // §11 Abs. 1 Z 6: Leistungsbeschreibung + Menge
  if (r.positionen.length === 0) {
    errors.push({ field: 'positionen', message: 'Keine Leistungspositionen angegeben (§11 (1) Z 6 UStG)' });
  }

  // §11 Abs. 1 Z 7: Entgelt + Steuersatz
  for (const p of r.positionen) {
    if (p.gesamtNetto <= 0) {
      errors.push({ field: `position_${p.leistungCode}`, message: `Position ${p.leistungCode}: Betrag muss > 0 sein` });
    }
  }

  return errors;
}

// ── Hilfsfunktionen Formatierung ──────────────────────────────────────────

export function formatEuroDisplay(cents: EuroCents): string {
  return (cents / 100).toLocaleString('de-AT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
}

export function formatDateDE(isoDate: string): string {
  if (!isoDate) { return ''; }
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y}`;
}
